/** Admin → Nominals. The accounts every payroll journal line posts to. */
import { useCallback, useEffect, useState } from "react";
import { getNominals, saveNominal, type NominalRow } from "../../payroll/api";

const ROLE_LABEL: Record<NominalRow["role"], string> = {
  gross: "Gross pay (incl holiday)",
  er_ni: "Employer NI",
  er_pension: "Employer pension",
  paye_control: "PAYE, Ee NI, Er NI, student loan",
  pension_control: "Employee & employer pension",
  sundry_control: "Post-tax sundry / AEO",
  net_control: "Net pay to bank",
};

export default function NominalsTab() {
  const [rows, setRows] = useState<NominalRow[] | null>(null);
  const [draft, setDraft] = useState<Record<string, NominalRow>>({});
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    try { setRows(await getNominals()); setDraft({}); } catch (e) { setError(msg(e)); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const edit = (r: NominalRow, patch: Partial<NominalRow>) =>
    setDraft((d) => ({ ...d, [r.account]: { ...r, ...d[r.account], ...patch } }));

  async function save(account: string) {
    const row = draft[account];
    if (!row) return;
    setError(null);
    try {
      await saveNominal(row);
      setNotice(`${account} updated.`);
      await load();
    } catch (e) { setError(msg(e)); }
  }

  return (
    <section className="panel">
      <header className="panel__head">
        <div>
          <h2>Payroll nominals</h2>
          <p className="panel__sub">
            Change one here and the next submission uses it — no account numbers are hard-coded in
            the app. Every change is audited.
          </p>
        </div>
        <button className="btn" onClick={() => void load()}>Refresh</button>
      </header>

      {error && <div className="alert alert--error">{error}</div>}
      {notice && !error && <div className="alert alert--ok">{notice}</div>}

      {rows === null ? <p className="muted">Loading…</p> : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr><th>Account</th><th>Name</th><th>Role in journal</th><th>Class</th><th>Side</th><th>Active</th><th /></tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const d = draft[r.account] ?? r;
                const dirty = !!draft[r.account];
                return (
                  <tr key={r.account}>
                    <td className="mono">{r.account}</td>
                    <td><input className="input" value={d.name} onChange={(e) => edit(r, { name: e.target.value })} /></td>
                    <td className="muted">{ROLE_LABEL[r.role]}</td>
                    <td>{r.class ?? "—"}</td>
                    <td>{r.side}</td>
                    <td>
                      <input type="checkbox" checked={d.active} onChange={(e) => edit(r, { active: e.target.checked })} />
                    </td>
                    <td>
                      <button className="btn btn--small" disabled={!dirty} onClick={() => void save(r.account)}>Save</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p className="muted">
        Deactivating a nominal that a journal role depends on will block the next submission with a
        clear message rather than posting to the wrong account.
      </p>
    </section>
  );
}

const msg = (e: unknown) => (e instanceof Error ? e.message : String(e));
